import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, FolderLock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { DocumentManager } from "@/components/DocumentManager";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const EMPTY = { nom: "", prenom: "", type: "sanitaire", default_vehicle_type: "", secu: "", caisse: "", taux: "", phone: "", email: "", address: "", access_note: "", notes: "" };

export default function Patients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [docPatient, setDocPatient] = useState(null);

  const load = () => api.get("/patients", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (editId) await api.put(`/patients/${editId}`, form);
      else await api.post("/patients", form);
      toast.success("Fiche enregistrée");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (id) => { await api.delete(`/patients/${id}`); load(); };
  const removePin = async () => {
    try {
      const { data } = await api.delete(`/patients/${editId}/home-pin`);
      setForm((f) => ({ ...f, home_pin: null, geo_source: "geocode", lat: data.lat, lon: data.lon }));
      toast.success("Point GPS supprimé — l'adresse écrite redevient la référence");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Patients & Clients</h1>
          <p className="text-muted-foreground text-sm">Giga-Fiche : base d'autocomplétion de la saisie flash.</p>
        </div>
        <Button onClick={openNew} data-testid="patients-add-button" className="gap-2"><Plus className="h-4 w-4" /> Ajouter</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9" data-testid="patients-search" />
      </div>

      <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="patients-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead><TableHead>Type</TableHead><TableHead>N° Sécu</TableHead>
              <TableHead>Caisse</TableHead><TableHead>Téléphone</TableHead><TableHead>Adresse</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucune fiche.</TableCell></TableRow>}
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`patient-row-${p.id}`}>
                <TableCell className="font-medium">{p.prenom} {p.nom}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.type === "prive" ? "bg-accent/20 text-amber-700 dark:text-amber-300 border-accent" : ""}>{p.type === "prive" ? "Privé" : "Sanitaire"}</Badge>
                  {p.default_vehicle_type && <Badge variant="outline" className="ml-1 text-[10px] border-primary text-primary" data-testid={`patient-vt-${p.id}`}>{p.default_vehicle_type}</Badge>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.secu || "—"}</TableCell>
                <TableCell className="text-sm">{p.caisse || "—"}</TableCell>
                <TableCell className="text-sm">{p.phone || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{p.address}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setDocPatient(p)} title="Documents" data-testid={`patient-docs-${p.id}`}><FolderLock className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`patient-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(p.id)} data-testid={`patient-delete-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouvelle"} fiche</DialogTitle>
            <DialogDescription className="sr-only">Formulaire de fiche patient.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} className="mt-1" data-testid="patient-prenom" /></div>
            <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1" data-testid="patient-nom" /></div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1" data-testid="patient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sanitaire">Sanitaire</SelectItem>
                  <SelectItem value="prive">Privé / Tourisme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Véhicule par défaut</Label>
              <Select value={form.default_vehicle_type || "auto"} onValueChange={(v) => set("default_vehicle_type", v === "auto" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="patient-default-vt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">— Automatique —</SelectItem>
                  {["Ambulance", "VSL", "Taxi", "TPMR"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="block text-[11px] text-muted-foreground mt-1">Pré-rempli à la saisie dans Jeregul.</span>
            </div>
            {form.type === "sanitaire" && (
              <>
                <div><Label>N° Sécurité sociale</Label><Input value={form.secu} onChange={(e) => set("secu", e.target.value)} className="mt-1" /></div>
                <div><Label>Caisse</Label><Input value={form.caisse} onChange={(e) => set("caisse", e.target.value)} className="mt-1" /></div>
                <div><Label>Taux de prise en charge</Label><Input value={form.taux} onChange={(e) => set("taux", e.target.value)} className="mt-1" placeholder="100%" /></div>
              </>
            )}
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
            {form.type === "prive" && <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>}
            <div className="col-span-2"><Label>Adresse du domicile</Label>
              <div className="mt-1">
                <AddressAutocomplete
                  value={form.address}
                  onChange={(txt) => set("address", txt)}
                  onSelect={(r) => set("address", r.label || r.address)}
                  placeholder="Nom, adresse ou lieu déjà connu (ex : EHPAD Les Cèdres…)"
                  testid="patient-address"
                  excludeType="Domicile patient"
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Les coordonnées GPS sont calculées automatiquement. Le domicile est enregistré comme « Domicile patient » dans l'annuaire — jamais partagé (RGPD).
              </p>
              {form.home_pin && (
                <div className="mt-2 rounded-md border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 flex items-start gap-2" data-testid="patient-home-pin">
                  <MapPin className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="text-xs flex-1">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-400">Point GPS relevé sur place</p>
                    <p className="text-muted-foreground tabular-nums">
                      {form.home_pin.lat?.toFixed(5)} · {form.home_pin.lon?.toFixed(5)}
                      {form.home_pin.accuracy_m ? ` (± ${Math.round(form.home_pin.accuracy_m)} m)` : ""}
                    </p>
                    <p className="text-muted-foreground">
                      par {form.home_pin.by_name || "un chauffeur"} le {new Date(form.home_pin.at).toLocaleDateString("fr-FR")} — sert de référence pour la navigation et les distances
                    </p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={removePin} data-testid="patient-remove-pin">
                    Supprimer
                  </Button>
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Consigne d'accès (visible par les chauffeurs)</Label>
              <Input value={form.access_note || ""} onChange={(e) => set("access_note", e.target.value)} className="mt-1"
                placeholder="Portail bleu au fond du chemin, sonner chez le voisin…" data-testid="patient-access-note" />
            </div>
            <div className="col-span-2"><Label>Consignes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="patient-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!docPatient} onOpenChange={(o) => !o && setDocPatient(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Justificatifs — {docPatient?.prenom} {docPatient?.nom}</DialogTitle>
            <DialogDescription className="sr-only">Gestion des documents justificatifs du patient.</DialogDescription>
          </DialogHeader>
          {docPatient && <DocumentManager mode="fiche" patientId={docPatient.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
